<script setup>
    import { Link } from '@inertiajs/vue3';
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
    import { faQuestion } from '@fortawesome/pro-solid-svg-icons';

    library.add(faQuestion);

    const props = defineProps({
        id: String,
        title: String,
        size: {
            type: String,
            default: undefined,
            validator(value) {
                // The value must match one of these strings
                return [undefined, 'xl', 'lg', 'md'].includes(value);
            }
        },
        icons: { type: Boolean, default: false },
        /**
         * The sections contains the following format.  If the title is
         * left as undefined no title will be used.
         *     [
         *         //If this is a normal menu (not iconized)
         *         {
         *             title: String,
         *             links: [{ title: String, url: String }]
         *         }
         *
         *         //If this is an icon menu
         *         {
         *             title: String,
         *             blurb: String,
         *             icon: String, //You will need to have included the icon in the calling component
         *             url: String
         *         }
         *     ]
         */
        sections: {
            type: Array,
            required: true,
            default: []
        }
    });

    const columnClass = 12 / (props.sections?.length ?? 1);
</script>
<template>
    <li class="nav-item dropdown">
        <a
            class="nav-link dropdown-toggle"
            role="button"
            :id="id"
            data-bs-toggle="dropdown"
            href="#"
            aria-haspopup="true"
            aria-expanded="false">
            {{ title }}
        </a>
        <div
            class="dropdown-menu"
            :class="{
                'dropdown-menu-xl p-0': size === 'xl',
                'dropdown-menu-lg': size === 'lg',
                'dropdown-menu-md': size === 'md'
            }"
            :aria-labelledby="id">
            <div v-if="!icons" class="row gx-0">
                <div
                    v-if="$slots.imageSection"
                    class="col-12"
                    :class="{ 'col-lg-6': sections.length > 0 }">
                    <slot name="imageSection" />
                </div>
                <div class="col-12 col-lg-6" v-if="sections.length">
                    <div class="dropdown-body">
                        <div class="row gx-0">
                            <div
                                :class="`col-12 col-lg-${columnClass}`"
                                v-for="(section, sectionIndex) of sections">
                                <!-- Heading -->
                                <h6
                                    v-if="section.title"
                                    class="dropdown-header"
                                    v-html="section.title" />

                                <!-- List -->
                                <Link
                                    v-for="(link, linkIndex) of section.links ?? []"
                                    class="dropdown-item"
                                    :class="{
                                        'mb-5':
                                            linkIndex === section.links.length - 1 && //The last link in a section
                                            sections.length > 1 &&
                                            sectionIndex % 2 !== 0 //The
                                    }"
                                    :href="link.url ?? '#'"
                                    v-html="link.title"
                                    role="link" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="list-group list-group-flush">
                <Link
                    v-for="section of sections"
                    class="list-group-item"
                    :href="section.url"
                    role="link"
                    :aria-label="section.title ?? ''">
                    <!-- Icon -->
                    <div class="icon icon-sm text-primary">
                        <font-awesome-icon
                            :icon="section.icon ?? ['fas', 'fa-question']"
                            size="2xl" />
                    </div>

                    <!-- Content -->
                    <div class="ms-4">
                        <!-- Heading -->
                        <h6
                            class="fw-bold text-uppercase text-primary mb-0"
                            v-html="section.title ?? ''" />
                        <!-- Text -->
                        <p class="fs-sm text-gray-700 mb-0" v-html="section.blurb ?? ''" />
                    </div>
                </Link>
            </div>
        </div>
    </li>
</template>
<script>
    export default {
        name: 'NavItemDropdown'
    };
</script>
